'use client';
import dynamic from 'next/dynamic';
import type { FC, PropsWithChildren } from 'react';
import { Suspense, useCallback, useMemo, useRef, useState } from 'react';

import { useAuthActionsContext } from '~context/auth';
import { type CheckoutReason, RegisterInbound } from '~features/annals';
import { annals } from '~features/annals/hooks';
import { NeuronsModal, SubscriptionModal } from '~features/paywall';
import { useHasSubscription } from '~features/tags';
import { useModal } from '~hooks/useModal';
import { usePurchase } from '~hooks/usePurchase';
import { useSession } from '~hooks/useSession';
import type {
	TShopItem,
	TShopNeuronsPack,
	TShopPromoCodeResponse,
	TShopSubscription,
} from '~services/api/credits/types';
import {
	Level,
	Logger,
	LoggerMessages,
	LoggerServices,
} from '~shared/services/logger';
import { convertToBackendId } from '~utils/ids';

import { PaywallContext } from './index';
import type { TPaymentConfig, TPaymentOpenConfig } from './types';
import { PaymentType } from './types';

const PaymentModal = dynamic(
	() =>
		import('../../components/Modals/PaymentModal').then(
			(mod) => mod.PaymentModal
		),
	{ ssr: false }
);

export const PaywallContextProvider: FC<PropsWithChildren> = ({ children }) => {
	const annalsSendOpenStore = annals.checkout.useOpenStore();
	//TODO: remove selectedItem state from provider. move to internal state
	const [selectedItem, setSelectedItem] = useState<TShopItem>();
	const [requestedCost, setRequestedCost] = useState(0);
	const paymentTypeRef = useRef<PaymentType>(undefined);
	const [defaultPromocode, setDefaultPromocode] = useState<
		string | undefined
	>();
	const [checkoutReason, setCheckoutReason] = useState<CheckoutReason>();
	const [botName, setBotName] = useState<string>();
	const [botId, setBotId] = useState<number | string>();
	const [promoCodeInfo, setPromoCodeInfo] = useState<
		TShopPromoCodeResponse | undefined
	>(undefined);

	const [hasSubscription] = useHasSubscription();
	const { isDemoUser } = useSession();
	const { openRegister } = useAuthActionsContext();

	const {
		isOpen: isNeuronsOpen,
		title: neuronsTitle,
		open: neuronsOpen,
		close: neuronsClose,
	} = useModal();
	const {
		isOpen: isSubscriptionOpen,
		title: subscriptionTitle,
		open: subscriptionOpen,
		close: subscriptionClose,
	} = useModal();

	const {
		purchasing,
		isPaymentFormOpened,
		tryToBuy,
		openPaymentForm,
		closePaymentForm,
	} = usePurchase({
		onPurchased() {
			closeNeurons(true);
			closeSubscription(true);
		},
	});

	const isShowPaymentForm = isPaymentFormOpened && !!selectedItem;

	const callbackDecorator =
		(originalCallback?: (isSuccess: boolean) => void) =>
		(isSuccess?: boolean) => {
			originalCallback?.(isSuccess || false);
		};

	const openSubscription = useCallback(
		(config: TPaymentOpenConfig) => () => {
			const {
				checkoutReason,
				botName,
				defaultPromocode,
				botClientId,
				title,
				paymentType,
				callback,
			} = config;

			paymentTypeRef.current = paymentType || PaymentType.Subscription;

			void annalsSendOpenStore({
				bot_UserId: botClientId,
				reason: checkoutReason,
				type: paymentTypeRef.current,
			});

			defaultPromocode && setDefaultPromocode(defaultPromocode);
			checkoutReason && setCheckoutReason(checkoutReason);
			botName && setBotName(botName);
			botClientId && setBotId(botClientId);
			return subscriptionOpen({ title, cb: callbackDecorator(callback) })();
		},
		[annalsSendOpenStore, subscriptionOpen]
	);

	const openNeurons = useCallback(
		(config: TPaymentOpenConfig) => () => {
			const {
				cost,
				checkoutReason,
				botName,
				botClientId,
				defaultPromocode,
				title,
				paymentType,
				callback,
			} = config;

			paymentTypeRef.current = paymentType || PaymentType.Neurons;
			void annalsSendOpenStore({
				bot_UserId: botClientId,
				reason: checkoutReason,
				type: paymentTypeRef.current,
			});

			defaultPromocode && setDefaultPromocode(defaultPromocode);
			cost && setRequestedCost(cost);
			checkoutReason && setCheckoutReason(checkoutReason);
			botName && setBotName(botName);
			botClientId && setBotId(botClientId);

			Logger.shared.log(
				Level.info,
				LoggerServices.PaymentService,
				LoggerMessages.BuyNeuronsScreenOpened,
				{ requiredCost: cost }
			);
			return neuronsOpen({ title, cb: callbackDecorator(callback) })();
		},
		[annalsSendOpenStore, neuronsOpen]
	);

	const openPaywall = useCallback(
		(config: TPaymentConfig) => {
			if (hasSubscription || config.prefer === 'neurons') {
				openNeurons({
					...config,
					// title: config.texts?.neurons,
				})();
			} else {
				const updatedCallback = (success: boolean) => {
					if (!success) {
						openNeurons({
							...config,
							title: config.texts?.neurons,
						})();
					}
				};

				openSubscription({
					...config,
					title: config.texts?.subscription,
					callback:
						config.prefer === 'subscription'
							? config.callback
							: updatedCallback,
				})();
			}
		},
		[hasSubscription, openNeurons, openSubscription]
	);

	const openPaywallWithDemoValidation = useCallback(
		(config: TPaymentConfig) => () => {
			if (isDemoUser) {
				openRegister({
					cb: () => {
						openPaywall(config);
					},
					analytics: { inbound: RegisterInbound.PaywallOpen },
				});
			} else {
				openPaywall(config);
			}
		},
		[openRegister, isDemoUser, openPaywall]
	);

	const clear = useCallback(() => {
		setDefaultPromocode(undefined);
		setBotName(undefined);
		setBotId(undefined);
		setRequestedCost(0);
		setSelectedItem(undefined);
		setCheckoutReason(undefined);
	}, []);

	const closeNeurons = useCallback(
		(success: boolean) => {
			clear();
			neuronsClose(success);
		},
		[clear, neuronsClose]
	);

	const closeSubscription = useCallback(
		(success: boolean) => {
			clear();
			subscriptionClose(success);
		},
		[clear, subscriptionClose]
	);

	const value = useMemo(
		() => ({
			isOpenPaymentForm: isPaymentFormOpened,
			reason: checkoutReason,
			botClientId: botId,
			openPaywall: openPaywallWithDemoValidation,
		}),
		[botId, checkoutReason, isPaymentFormOpened, openPaywallWithDemoValidation]
	);

	const closeAllModals = useCallback(() => {
		closePaymentForm();
		neuronsClose(true);
		subscriptionClose(true);
		clear();
	}, [closePaymentForm, neuronsClose, subscriptionClose, clear]);

	const closeSubscriptionViaCloseButton = useCallback(() => {
		closeSubscription(false);
	}, [closeSubscription]);

	const closeNeuronsViaCloseButton = useCallback(() => {
		closeNeurons(false);
	}, [closeNeurons]);

	const buy = useCallback(() => {
		selectedItem &&
			void tryToBuy(selectedItem, promoCodeInfo, {
				reason: checkoutReason,
				reasonType: paymentTypeRef.current,
				botId: botId ? convertToBackendId(botId) : undefined,
			});
	}, [botId, checkoutReason, promoCodeInfo, selectedItem, tryToBuy]);

	return (
		<PaywallContext.Provider value={value}>
			{children}
			<SubscriptionModal
				open={isSubscriptionOpen}
				locked={false}
				botName={botName}
				defaultPromocode={defaultPromocode}
				title={subscriptionTitle}
				selectedItem={selectedItem as TShopSubscription}
				promoCodeInfo={promoCodeInfo}
				purchasing={purchasing}
				onSelect={setSelectedItem}
				onPromoCodeApply={setPromoCodeInfo}
				onBuy={buy}
				onBuyWithForm={openPaymentForm}
				onClose={closeSubscriptionViaCloseButton}
			/>
			<NeuronsModal
				open={isNeuronsOpen}
				locked={false}
				title={neuronsTitle}
				defaultPromocode={defaultPromocode}
				selectedItem={selectedItem as TShopNeuronsPack}
				promoCodeInfo={promoCodeInfo}
				requestedCost={requestedCost}
				purchasing={purchasing}
				onSelect={setSelectedItem}
				onPromoCodeApply={setPromoCodeInfo}
				onBuy={buy}
				onBuyWithForm={openPaymentForm}
				onClose={closeNeuronsViaCloseButton}
			/>
			{isShowPaymentForm && (
				<Suspense fallback={null}>
					<PaymentModal
						item={selectedItem}
						promoCodeInfo={promoCodeInfo}
						checkoutReason={checkoutReason}
						botClientId={botId}
						paymentType={paymentTypeRef.current}
						botName={botName}
						onSuccess={closeAllModals}
						onClose={closePaymentForm}
					/>
				</Suspense>
			)}
		</PaywallContext.Provider>
	);
};
